<template>
  <div
    class="nicu-invoice"
    style="display: none"
  >
    <div class="invoice-container">
      <!-- Header -->
      <div class="invoice-header">
        <h2>{{ $t("incubator.nicu-invoices") }}</h2>
        <p class="hospital">
          {{ $brand.nameArShort }}
        </p>

        <div class="invoice-meta">
          <div class="invoice-number">
            {{ $t("common.invoice-no") }}:
            <span>#{{ data.invoice_no || "N/A" }}</span>
          </div>

          <div class="invoice-date">
            {{ $t("common.date") }}:
            {{ formatDate(data.created_at) }}
          </div>
        </div>
      </div>

      <!-- Patient Information (Refactored) -->
      <div class="patient-box">
        <div>
          <strong>{{ $t("common.patient") }}:</strong>
          {{ formatPatientName() }}
        </div>
        <div>
          <strong>{{ $t("reception.phone") }}:</strong>
          {{ getPatientPhone() }}
        </div>
        <div>
          <strong>{{ $t("reception.companion") }}:</strong>
          {{ getCompanionName() }}
        </div>
        <div>
          <strong>{{ $t("reception.companion-phone") }}:</strong>
          {{ getCompanionPhone() }}
        </div>
      </div>

      <!-- Invoice Items -->
      <table class="invoice-table">
        <thead>
          <tr>
            <th>{{ $t("common.description") }}</th>
            <th class="right">
              {{ $t("employee.amount") }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{{ incubatorName }}</td>
            <td class="right">
              <!-- If there is a discount, show net amount -->
              <span v-if="hasDiscount()">
                {{ formatPrice(getNetAmount()) }}
              </span>
              <span v-else>
                {{ formatPrice(data.amount) }}
              </span>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <!-- Show discount row only when discount exists -->
          <tr
            v-if="hasDiscount()"
          >
            <td>{{ $t("common.discount-amount") || "الخصم" }}</td>
            <td class="right">
              {{ formatPrice(data.discount) }}
            </td>
          </tr>
          <tr class="total-row">
            <td>
              <span v-if="hasDiscount()">
                {{ $t("common.amount-after-discount") || "الإجمالي بعد الخصم" }}
              </span>
              <span v-else>
                {{ $t("common.total") }}
              </span>
            </td>
            <td class="right">
              <span v-if="hasDiscount()">
                {{ formatPrice(getNetAmount()) }}
              </span>
              <span v-else>
                {{ formatPrice(data.amount) }}
              </span>
            </td>
          </tr>
        </tfoot>
      </table>

      <!-- Footer -->
      <div class="invoice-footer">
        {{ $t("common.thank-you") }} —
        {{ $t("common.printed-on") }} {{ getCurrentDate() }}
      </div>
    </div>
  </div>
</template>

<script>
import { getNestedProperty } from '@/utils/nestedProperty.js'

export default {
  props: {
    data: Object,
    incubatorName: String,
  },

  methods: {
    getNestedProperty,

    formatPrice(value) {
      return `${Number(value || 0).toLocaleString()} IQD`
    },

    hasDiscount() {
      const discount = Number(this.data && this.data.discount ? this.data.discount : 0)
      return discount > 0
    },

    getNetAmount() {
      const amount = Number(this.data && this.data.amount ? this.data.amount : 0)
      const discount = Number(this.data && this.data.discount ? this.data.discount : 0)
      const net = amount - discount
      return net > 0 ? net : 0
    },

    formatPatientName() {
      return getNestedProperty(
        this.data,
        'patient_card.patient.name',
        'N/A',
      )
    },

    getPatientPhone() {
      return getNestedProperty(
        this.data,
        'patient_card.patient.phone',
        'N/A',
      )
    },

    getCompanionName() {
      return getNestedProperty(
        this.data,
        'patient_card.full_companion_name',
        'N/A',
      )
    },

    getCompanionPhone() {
      return getNestedProperty(
        this.data,
        'patient_card.companion_phone',
        'N/A',
      )
    },

    formatDate(d) {
      if (!d) return '—'
      return new Date(d).toLocaleDateString('en-US')
    },

    getCurrentDate() {
      return new Date().toLocaleString('en-US')
    },
  },
}
</script>

  <style>
    .nicu-invoice {
      display: none;
    }

    .invoice-container {
      font-family: Arial, sans-serif;
      padding: 12px;
      width: 100%;
    }

    /* Header */
    .invoice-header {
      text-align: center;
      border-bottom: 2px solid #999;
      padding-bottom: 10px;
      margin-bottom: 15px;
    }

    .invoice-header h2 {
      margin: 0;
      font-size: 22px;
    }

    .hospital {
      font-size: 15px;
      margin: 4px 0;
    }

    .invoice-meta {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 14px;
      margin-top: 10px;
    }

    .invoice-number {
      font-size: 18px;
      font-weight: bold;
      border: 2px solid #999;
      padding: 6px 12px;
    }

    .invoice-number span {
      font-size: 20px;
    }

    .invoice-date {
      font-size: 14px;
    }

    /* Patient Info */
    .patient-box {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px 20px;
      border: 1px solid #aaa;
      padding: 10px;
      margin-bottom: 18px;
      font-size: 15px;
    }

    /* Table */
    .invoice-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 16px;
    }

    .invoice-table th,
    .invoice-table td {
      border: 1px solid #aaa;
      padding: 10px;
    }

    .invoice-table th {
      background: #f5f5f5;
      font-weight: bold;
    }

    .right {
      text-align: right;
    }

    /* Total */
    .total-row td {
      font-size: 17px;
      font-weight: bold;
    }

    /* Footer */
    .invoice-footer {
      margin-top: 20px;
      text-align: center;
      font-size: 13px;
      border-top: 1px solid #aaa;
      padding-top: 8px;
    }

    @media print {
      .nicu-invoice {
        display: block !important;
      }

      body {
        margin: 0;
      }
    }
  </style>
